import { useState } from 'react';

import { useMediaQuery, IconButton, Popper, Fade } from '@mui/material';
import Phone from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useTheme, styled } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import { useI18n } from '@openclassrooms/translations/I18nProvider';

import browser from '@openclassrooms/utils/browser';

const PhoneNumber = styled(Typography)(({ theme: { palette, typography } }) => ({
  color: palette.text.link,
  fontSize: typography.pxToRem(14),
  lineHeight: typography.pxToRem(24),
  display: 'block',
}));

const ContactList = styled('ul')(({ theme: { breakpoints, palette, spacing } }) => ({
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  [breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
  '& > li': {
    listStyle: 'none',
    padding: spacing(0, 1),
    borderRight: `1px solid ${palette.layout.line.divider}`,
    '&:first-of-type': {
      paddingLeft: 0,
    },
    '&:last-child': {
      border: 'none',
    },
    '.button-icon': {
      color: palette.text.primary,
    },
    [breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      border: 'none',
      padding: 0,
      '.button-icon': {
        marginRight: spacing(1),
      },
      '.caption-wrapper': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        borderLeft: `1px solid ${palette.layout.line.divider}`,
        paddingLeft: spacing(1),
        marginBottom: spacing(1),
        minHeight: 44,
      },
    },
  },
}));

const Caption = styled(Typography)(({ theme: { typography } }) => ({
  fontSize: typography.pxToRem(12),
  lineHeight: typography.pxToRem(22),
}));

const PopOverContent = styled(Typography)(({ theme: { spacing, palette, zIndex } }) => ({
  backgroundColor: palette.background.primary,
  padding: spacing(0, 2),
  marginLeft: spacing(2),
  maxWidth: 160,
  zIndex: zIndex.modal,
}));

const FooterContacts = () => {
  const { i18n, translate } = useI18n();
  const isLocatedInFr = i18n.country === 'FR';
  const { breakpoints } = useTheme();
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen((prevOpen) => !prevOpen);
  };

  const handleClickOnPhone = () => {
    browser.window.open(`tel:${translate('footer.contact.phone.french_number')}`, '_parent');
  };

  const zendeskRequestPageUrl =
    i18n.lang === 'fr'
      ? 'https://openclassrooms.zendesk.com/hc/fr/requests/new'
      : 'https://openclassrooms.zendesk.com/hc/en-us/requests/new';

  const phoneNumberContent = (
    <>
      <PhoneNumber
        variant="body"
        component="a"
        href={`tel:${translate('footer.contact.phone.french_number')}`}
        data-event="Footer|Click|ClickToCall"
      >
        {translate('footer.contact.phone.french_number')}
      </PhoneNumber>
      <Caption variant="body" component="span" sx={{ color: 'text.secondary' }}>
        {translate('footer.contact.phone.time_hours')}
      </Caption>
    </>
  );

  return (
    <>
      <ContactList>
        {isLocatedInFr && (
          <li>
            <IconButton
              className="button-icon"
              onClick={isMobile ? handleClickOnPhone : handleClick}
              aria-label={`${translate('footer.contact.phone.french_number')} ${translate(
                'footer.contact.phone.time_hours'
              )}`}
              data-event={isMobile ? 'Footer|Click|ClickToCall' : 'Footer|Click|ViewPhoneNumber'}
              size="large"
            >
              <Phone />
            </IconButton>
            <Popper
              open={isMobile ? false : isOpen}
              anchorEl={anchorEl}
              placement="right-start"
              transition
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <PopOverContent>{phoneNumberContent}</PopOverContent>
                </Fade>
              )}
            </Popper>
            {isMobile && <div className="caption-wrapper">{phoneNumberContent}</div>}
          </li>
        )}
        <li>
          <IconButton
            className="button-icon"
            href={zendeskRequestPageUrl}
            target="_blank"
            aria-label={translate('footer.contact.mail.caption')}
            size="large"
          >
            <EmailIcon />
          </IconButton>
          {isMobile && (
            <Caption variant="body" component="span" className="caption-wrapper">
              {translate('footer.contact.mail.caption')}
            </Caption>
          )}
        </li>
      </ContactList>
    </>
  );
};

export default FooterContacts;
